/**
 * UploadFlyout — Reusable upload button + flyout popover with three options:
 * Upload Image, Upload Video, Upload Audio.
 *
 * Extracted from ChatInput.tsx for reuse in ReferenceSelector and other contexts.
 * Encapsulates toggle state, refs, outside-click dismiss, three hidden file inputs,
 * and the flyout popover JSX.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Plus, Upload } from 'lucide-react'

export interface UploadFlyoutProps {
  onUploadFile: (file: File, type: 'image' | 'video' | 'audio') => void
  disabled?: boolean
  size?: 'sm' | 'md'
  testIdPrefix?: string
  label?: string
  buttonClassName?: string
  title?: string
  icon?: 'upload' | 'plus'
}

const UploadFlyout = memo(function UploadFlyout({
  onUploadFile,
  disabled = false,
  size = 'md',
  testIdPrefix = 'upload',
  label,
  buttonClassName,
  title = 'Upload file',
  icon = 'upload',
}: UploadFlyoutProps) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target
      if (
        target instanceof globalThis.Node &&
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showMenu])

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'audio') => {
      const file = e.target.files?.[0]
      if (file) {
        onUploadFile(file, type)
      }
      // Reset the input so the same file can be selected again
      e.target.value = ''
      setShowMenu(false)
    },
    [onUploadFile],
  )

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'
  const buttonPadding = size === 'sm' ? 'p-1.5' : 'p-2'
  const Icon = icon === 'plus' ? Plus : Upload

  return (
    <div className="relative">
      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileSelect(e, 'image')}
        className="hidden"
        data-testid={`${testIdPrefix}-image-input`}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={(e) => handleFileSelect(e, 'video')}
        className="hidden"
        data-testid={`${testIdPrefix}-video-input`}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        onChange={(e) => handleFileSelect(e, 'audio')}
        className="hidden"
        data-testid={`${testIdPrefix}-audio-input`}
      />

      {/* Upload button */}
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setShowMenu((prev) => !prev)}
        className={buttonClassName || `${buttonPadding} rounded-md text-text-muted hover:text-text-secondary hover:bg-surface-2 transition-colors disabled:opacity-50`}
        title={title}
        data-testid={`${testIdPrefix}-button`}
      >
        <Icon className={iconSize} strokeWidth={1.5} />
        {label && <span>{label}</span>}
      </button>

      {/* Flyout popover */}
      {showMenu && (
        <div
          ref={menuRef}
          className="absolute bottom-full left-0 mb-2 bg-surface-1 border border-border-subtle rounded-lg shadow-lg z-50 min-w-[160px] py-1"
          data-testid={`${testIdPrefix}-menu`}
        >
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 transition-colors"
            onClick={() => imageInputRef.current?.click()}
            data-testid={`${testIdPrefix}-image-option`}
          >
            <svg
              className="w-4 h-4 text-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Upload Image
          </button>
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 transition-colors"
            onClick={() => videoInputRef.current?.click()}
            data-testid={`${testIdPrefix}-video-option`}
          >
            <svg
              className="w-4 h-4 text-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Upload Video
          </button>
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 transition-colors"
            onClick={() => audioInputRef.current?.click()}
            data-testid={`${testIdPrefix}-audio-option`}
          >
            <svg
              className="w-4 h-4 text-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
            Upload Audio
          </button>
        </div>
      )}
    </div>
  )
})

UploadFlyout.displayName = 'UploadFlyout'
export { UploadFlyout }
